import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchClientBranding, type ClientBranding } from "@/api";
import { useDashboard } from "@/hooks/useDashboard";

/**
 * Resolves the active tenant for cobrand purposes.
 *
 * Order of precedence (per ADR-20260427-client-dashboard-branding-cobrand):
 *   1. Subdomain `{slug}.growtify.app` (production deploy mode)
 *   2. URL `?tenant=` filter (preview mode — same cockpit, scoped view)
 *   3. None → default Growtify branding
 *
 * Returns:
 *   - branding: full ClientBranding (or null while loading)
 *   - isCobranded: true when a real (non-fallback) tenant resolved
 *
 * Side effect: applies CSS variables `--accent-primary` + `--accent-secondary`
 * + `--accent-ring` on `<html>` element so any Tailwind class using these vars
 * picks up the per-tenant accent.
 */
function detectTenantFromHost(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  // Patterns we recognize as tenant subdomains:
  //   {slug}.growtify.app          → 'slug'
  //   cockpit.growtify.app         → null (cockpit itself, not a tenant)
  //   ops.growtify.app             → null (legacy ops)
  //   {anything}.preview.growtify.app → null (CF Pages preview)
  if (!host.endsWith(".growtify.app")) return null;
  const sub = host.slice(0, -".growtify.app".length);
  if (!sub || sub === "cockpit" || sub === "ops" || sub.includes(".")) return null;
  return sub;
}

export function useClientBranding(): {
  branding: ClientBranding | null;
  isLoading: boolean;
  isCobranded: boolean;
  resolvedTenant: string | null;
} {
  const { selectedTenant } = useDashboard();
  // Subdomain takes precedence over URL ?tenant= param
  const subdomainTenant = detectTenantFromHost();
  const resolvedTenant = subdomainTenant || selectedTenant;

  const branding = useQuery({
    queryKey: ["client-branding", resolvedTenant || "_default"],
    queryFn: () => fetchClientBranding(resolvedTenant || "_default"),
    enabled: true,
    staleTime: 5 * 60_000, // 5 min — branding rarely changes mid-session
  });

  const data = branding.data?.branding || null;
  const isCobranded = !!(data && !data._is_fallback);

  // Inject CSS variables — overrides Tailwind accent color globally
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (data && isCobranded) {
      root.style.setProperty("--accent-primary", data.accent_color_primary);
      root.style.setProperty("--accent-secondary", data.accent_color_secondary);
    } else {
      // Reset to Growtify defaults
      root.style.removeProperty("--accent-primary");
      root.style.removeProperty("--accent-secondary");
    }
  }, [data, isCobranded]);

  return {
    branding: data,
    isLoading: branding.isLoading,
    isCobranded,
    resolvedTenant,
  };
}
